import { useMemo } from "react"

import { useHumanReviewProjectsLinkedSimulationIdsTransport } from "@/human-feedback/transport"

import { useConversationsFilter } from "../contexts/ConversationsFilterContext"
import { resolveConversationsSimulationOutputRestriction } from "../utils/conversationsProjectFilter"

/**
 * Resolves the active human review project filter, together with any
 * visualizer cluster ids, into the `simulation_output_ids` restriction for the
 * conversation keyset request. `isPending` holds the request back until the
 * selected projects' linked conversations are known.
 */
export function useConversationsSimulationOutputRestriction({
  clusterIds,
  enabled = true,
}: {
  clusterIds?: string[]
  enabled?: boolean
} = {}) {
  const {
    state: { projectIds },
  } = useConversationsFilter()
  const hasProjectFilter = projectIds.length > 0
  const linkedIdsQuery = useHumanReviewProjectsLinkedSimulationIdsTransport({
    projectIds,
    enabled,
  })
  const projectLinkedIds = hasProjectFilter ? linkedIdsQuery.data : undefined

  const restriction = useMemo(
    () =>
      resolveConversationsSimulationOutputRestriction({
        projectLinkedIds,
        clusterIds,
      }),
    [projectLinkedIds, clusterIds],
  )

  const isError =
    hasProjectFilter && linkedIdsQuery.isError && projectLinkedIds === undefined

  return {
    ...restriction,
    hasProjectFilter,
    error: isError ? linkedIdsQuery.error : null,
    isError,
    isFetching: hasProjectFilter && linkedIdsQuery.isFetching,
    isPending: hasProjectFilter && !isError && projectLinkedIds === undefined,
    refetch: linkedIdsQuery.refetch,
  }
}
